import React, { useState, useMemo, useEffect } from 'react';
import { View, ScrollView, StyleSheet, useWindowDimensions, ActivityIndicator } from 'react-native';
import { useTheme } from '@react-navigation/native';
import CustomLabel from '../../../components/CustomLabel/CustomLabel';
import { useAuthContext } from "../../../hooks/useAuthContext";
import CustomDropDown from '../../../components/CustomDropDown/CustomDropdown';
import CustomButton from '../../../components/CustomButton';
import { useTranslation } from 'react-i18next';
import { useMyTimesheets } from '../../../hooks/useMyTimesheets';
import { Checkbox } from "react-native-paper";
import { showMessage } from 'react-native-flash-message';

const TimesheetTable = ({
    usersTsList,
    MONTH,
    setModalCopyTSLinesVisible,
    toTS,
    loadTimesheetDetails
}) => {
    //#region Const Definitions
    const { t } = useTranslation();
    const { width: screenWidth } = useWindowDimensions();
    const { user, clientApi, username } = useAuthContext();
    const { colors } = useTheme()
    const styles = makeStyles(colors);
    const { loadNonChargeableLines, loadChargeableLines, copyTSLines } = useMyTimesheets();

    const [selectedMonth, setSelectedMonth] = useState('All Months');
    const [fromTSError, setFromTSError] = useState(false);
    const [tsLines, setTSLines] = useState([]);
    const [selectedLines, setSelectedLines] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const monthOptions = [{ label: 'All Months', value: 'All Months' },
        ...MONTH.map(m => ({ label: m.description, value: m.value }))];

    useEffect(() => {
        if (!usersTsList || usersTsList.length === 0) return;
        
        const fetchLines = async () => {
            setIsLoading(true);
            try {
                const combinedLines = [];

                for (const ts of usersTsList) {
                    const tsnoUI = ts.tsnoUI;
                    const month = ts.month;

                    // Load chargeable lines
                    const chargeableLines = await loadChargeableLines( tsnoUI);
                    if (Array.isArray(chargeableLines)) {
                        chargeableLines.forEach(line => {
                            combinedLines.push({
                                rowguid: line.rowguid,
                                tsnoui: line.tsnoui,
                                jobno: line.jobno,
                                name: line.name,
                                phasecode: line.phasecode,
                                description: line.description,
                                isChargeable: true,
                                month: month,
                            });
                        });
                    }

                    // Load non-chargeable lines
                    const nonChargeableLines = await loadNonChargeableLines(  tsnoUI);
                    if (Array.isArray(nonChargeableLines)) {
                        nonChargeableLines.forEach(line => {
                            combinedLines.push({
                                rowguid: line.rowguid,
                                tsnoui: line.tsnoui,
                                jobno: line.jobno,
                                name: line.name,
                                phasecode: line.phasecode,
                                description: line.description,
                                isChargeable: false,
                                month: month,
                            });
                        });
                    }
                }

                setTSLines(combinedLines);
            } catch (error) {
                console.error('Error loading timesheet lines:', error);
            } finally {
                setIsLoading(false)
            }
        };

        fetchLines();
    }, [usersTsList]);

    return (
        <View style={{ padding: 20, height: screenWidth*0.3 }}>
            <View style={{ paddingVertical: 20, alignItems: 'center' }} >
                <CustomLabel value={t('common:copyTimesheetLines')} type="TITLE" />
            </View>
            {/* Month dropdown */}
            <View style={{ flexDirection: 'row', paddingVertical: 10 }}>
                <View style={{ alignItems: 'flex-start', marginHorizontal: 20 }}>
                    <CustomLabel value={t('common:month') + ':'} type="SUBTITLE" />
                </View>
                <CustomDropDown
                    options={monthOptions}
                    defValue={selectedMonth}
                    value={selectedMonth}
                    setValue={setSelectedMonth}
                    type={"MYPROFILE"}
                />
            </View>
            {/* Display lines based on selected Month */}
            <View style={{ flex: 1 }}>
                <ScrollView style={styles.container}>
                    {/* Header Row */}
                    <View style={[styles.row, styles.headerRow]}>
                        <View style={[styles.cell, { width: '15%' }]}>
                            <CustomLabel value={t('common:description')} type="SUBTITLE" />
                        </View>
                        <View style={[styles.cell, { width: '30%' }]}>
                            <CustomLabel value={t('common:jobName')} type="SUBTITLE" />
                        </View>
                        <View style={[styles.cell, { width: '15%' }]}>
                            <CustomLabel value={t('common:jobCode')} type="SUBTITLE" />
                        </View>
                        <View style={[styles.cell, { width: '15%' }]}>
                            <CustomLabel value={t('common:phaseCode')} type="SUBTITLE" />
                        </View>
                        <View style={[styles.cell, { width: '10%' }]}>
                            <CustomLabel value={t('common:chargable')} type="SUBTITLE" />
                        </View>
                        {/* Checkbox column header */}
                        <View style={[styles.cell_last, { width: '15%', alignItems: 'center' }]}>
                            <CustomLabel value={t('common:selected') + (fromTSError ? '*' : '')} type={fromTSError ? 'EDIT_EMPLOYEE_BAD_STATE' : "SUBTITLE"} />
                        </View>
                    </View>

                    {isLoading ?
                        <View style={styles.loader}>
                            <ActivityIndicator animating={true} color={colors.blue} />
                        </View>
                    :
                        <>
                        {/* Data Rows */}
                        {tsLines
                            .filter(item => selectedMonth === 'All Months' || item.month === selectedMonth)
                            .map((item, index) => {
                                const isChecked = selectedLines.includes(item.rowguid);

                                const toggleSelection = () => {
                                    if (isChecked) {
                                        setSelectedLines(prev => prev.filter(id => id !== item.rowguid));
                                    } else {
                                        setSelectedLines(prev => [...prev, item.rowguid]);
                                    }
                                };

                                return (
                                    <View key={item.rowguid || index} style={styles.row}>
                                        <View style={[styles.cell, { width: '15%' }]}>
                                            <CustomLabel value={item.description || ''} type="SUBTITLE" />
                                        </View>
                                        <View style={[styles.cell, { width: '30%' }]}>
                                            <CustomLabel value={item.name || ''} type="SUBTITLE" />
                                        </View>
                                        <View style={[styles.cell, { width: '15%' }]}>
                                            <CustomLabel value={item.jobno || ''} type="SUBTITLE" />
                                        </View>
                                        <View style={[styles.cell, { width: '15%' }]}>
                                            <CustomLabel value={item.phasecode || ''} type="SUBTITLE" />
                                        </View>
                                        <View style={[styles.cell, { width: '10%' }]}>
                                            <CustomLabel value={item.isChargeable ? t('common:yes') : t('common:no')} type="SUBTITLE" />
                                        </View>
                                        <View style={[styles.cell_last, { width: '15%', justifyContent: 'center', alignItems: 'center' }]}>
                                            <Checkbox
                                                status={isChecked ? 'checked' : 'unchecked'}
                                                onPress={toggleSelection}
                                            />
                                        </View>
                                    </View>
                                );
                            })}
                            </>
                        }
                </ScrollView>
            </View>
            {/* Copy & Cancel Buttons */}
            <View style={{ flexDirection: 'row', justifyContent: 'center', paddingHorizontal: 50 }}>
                <CustomButton
                    text={t('common:copy')}
                    onPress={() => {
                        if (selectedLines.length <= 0) {
                            setFromTSError(true);
                            return;
                        }
                        setFromTSError(false);
                        copyTSLines( toTS, selectedLines)
                        .then((res) => {
                            if (res === 200) {
                                console.log(res)
                                showMessage({
                                    message: t("common:copyTSSuccess"),
                                    type: "success",
                                    duration: 3000,
                                });
                                loadTimesheetDetails(toTS);
                            } else {
                                showMessage({
                                    message: t("common:errorcopy"),
                                    type: "danger",
                                    duration: 3000,
                                });
                            }
                        })
                        .catch((error) => {
                            showMessage({
                                message: t("common:errorcopy"),
                                type: "danger",
                                duration: 3000,
                            });
                        })
                        setModalCopyTSLinesVisible(false);
                    }}
                    type="BUTTON_SAVE"
                    textTop='0'
                    isGradient={true}
                    sz={15}
                />
                <CustomButton
                    text={t('common:cancel')}
                    onPress={() => setModalCopyTSLinesVisible(false)}
                    type="BUTTON_CANCEL"
                    textTop="0"
                    sz={15}
                />
            </View>
        </View>
    );
};

const makeStyles = (colors) => StyleSheet.create({
    container: {
        flexDirection: 'column',
        borderWidth: 1,
        borderColor: '#ddd',
    },
    row: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#ddd',
        alignItems: 'center',
    },
    headerRow: {
        backgroundColor: '#dcdcdc',
    },
    cell: {
        padding: 8,
        borderRightWidth: 1,
        borderRightColor: '#ddd',
        justifyContent: 'center',
        borderRightHeight: '100%'
    },
    cell_last: {
        padding: 8,
        justifyContent: 'center',
    },
    cell_dropdown: {
        padding: 8,
        borderRightWidth: 1,
        borderRightColor: '#ddd',
        justifyContent: 'center',
        borderRightHeight: '100%',
        paddingTop: 22.5,
        paddingBottom: 22.5
    },
    loader: {
        flex: 1,
        marginTop: "1%",
        marginLeft: "2%",
        marginRight: "2%",
    },
});

export default TimesheetTable;